import { RefObject, useEffect, useRef, useState } from "react";

import {
    EditOutlined,
    EditTwoTone,
    FileTextOutlined,
    ReadOutlined,
    ScissorOutlined,
    UnorderedListOutlined,
} from "@ant-design/icons";
import {
    TFunction,
    Trans,
    useTranslation,
} from "@impero-com/common/utils/i18n";
import {
    Button,
    AIPromptInput,
    AIPromptResponse,
    AIQuickPrompts,
    AIResult,
    AIUserPromptAndResponse,
    Drawer,
    OverflowText,
    renderMarkdown,
    Tooltip,
} from "@impero-com/ui";
import { rawPalette, rawTransition } from "@impero-com/ui/styles/theme.css";
import { AnimatePresence, motion } from "motion/react";
import { Control, UseFormSetValue, useWatch } from "react-hook-form";

import { usePostControlsAiEditDescription } from "api/generated/controls/controls";
import type { DescriptionSelection, PromptResponse } from "api/generated/model";
import {
    MarkdownEditorMethods,
    MarkdownSelectionRange,
} from "components/markdownEditor/types";

import { ControlViewFormSchema } from "../formSchema";

import * as styles from "./SuggestAiDescriptionButton.css";

const PREVIEW_MAX_COLLAPSED_HEIGHT = 120;

interface SuggestAiDescriptionButtonProps {
    control: Control<ControlViewFormSchema>;
    setValue: UseFormSetValue<ControlViewFormSchema>;
    drawerOpen: boolean;
    setDrawerOpen: (open: boolean) => void;
    editorRef: RefObject<MarkdownEditorMethods | null>;
    selection?: MarkdownSelectionRange;
    disabled?: boolean;
}

export function SuggestAiDescriptionButton({
    control,
    setValue,
    drawerOpen,
    setDrawerOpen,
    editorRef,
    selection,
    disabled,
}: SuggestAiDescriptionButtonProps) {
    const { t } = useTranslation();
    const [controlTitle, controlProgramId, description] = useWatch({
        control,
        name: ["title", "controlProgramId", "description"],
    });
    const buttonText = getButtonText(
        t,
        description,
        selection?.selectedMarkdown
    );
    const buttonDisabled =
        disabled || !controlTitle.trim() || !controlProgramId;
    const isEditing = description.length > 0;
    const previewText = selection?.selectedMarkdown ?? description;
    const showPreview = isEditing;

    const responseContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const [previewCollapsed, setPreviewCollapsed] = useState(true);
    const [conversations, setConversations] = useState<
        AIUserPromptAndResponse[]
    >([]);
    const onSuccess = (data: AIResult, id: string) =>
        setConversations((prev) => withResult(prev, id, data));
    const onError = (id: string) =>
        setConversations((prev) => withError(prev, id));
    const { mutate, isPending } = usePostControlsAiEditDescription({
        mutation: { retry: 3 },
    });
    const focusInput = () => inputRef.current?.focus();
    const scrollToBottom = () => {
        responseContainerRef.current?.scrollTo({
            top: responseContainerRef.current.scrollHeight,
            behavior: "smooth",
        });
    };

    useEffect(() => {
        if (!isPending) {
            focusInput();
        }
    }, [isPending]);

    const handleDrawerOpenChange = (open: boolean) => {
        if (!open) {
            setConversations([]);
        }
        setDrawerOpen(open);
    };

    const submitPrompt = (prompt: string) => {
        const id = crypto.randomUUID();
        setConversations((prev) => [...prev, { id, userPrompt: prompt }]);

        if (!controlProgramId) {
            return;
        }

        const data = {
            prompt,
            description,
            selection: toSelectionPayload(selection),
            title: controlTitle,
            chatHistory: toChatHistory(conversations),
            controlProgramId,
        };

        mutate(
            { data },
            {
                onSuccess: (response) => onSuccess(response.data, id),
                onError: () => onError(id),
            }
        );
    };

    const handleRevert = (id: string) => {
        setConversations((prev) => {
            const idx = prev.findIndex((c) => c.id === id);
            if (idx === -1) {
                return prev;
            }
            return prev.slice(0, idx);
        });
    };

    const handleRetry = (id: string) => {
        const entry = conversations.find((c) => c.id === id);
        if (!entry) {
            return;
        }

        setConversations((prev) => withLoading(prev, id));

        const idx = conversations.findIndex((c) => c.id === id);
        const historyUpToRetry = toChatHistory(conversations.slice(0, idx));

        if (!controlProgramId) {
            return;
        }

        const data = {
            prompt: entry.userPrompt,
            description,
            selection: toSelectionPayload(selection),
            title: controlTitle,
            chatHistory: historyUpToRetry,
            controlProgramId,
        };

        mutate(
            { data },
            {
                onSuccess: (response) => onSuccess(response.data, id),
                onError: () => onError(id),
            }
        );
    };

    const handleInsert = (id: string) => {
        const entry = conversations.find((c) => c.id === id);
        // Note that we do not show the insert button for empty responses, so the kind should never be "empty" in this case.
        if (entry?.result && entry.result.kind !== "empty") {
            const replacement = entry.result.response;
            const newDescription = selection
                ? description.slice(0, selection.start) +
                  replacement +
                  description.slice(selection.end)
                : replacement;
            setValue("description", newDescription, { shouldDirty: true });
            // MarkdownInput isn't a fully controlled editor; imperatively sync content.
            editorRef.current?.setContent(newDescription);
        }
        handleDrawerOpenChange(false);
    };

    const quickPrompts = (
        isEditing ? getEditingPrompts(t) : getSuggestPrompts(t)
    ).map((prompt) => ({
        ...prompt,
        onClick: () => submitPrompt(prompt.label),
    }));

    const button = (
        <Button
            className={styles.button}
            variant="link"
            disabled={buttonDisabled}
            size="small"
            icon={
                <EditTwoTone
                    twoToneColor={
                        buttonDisabled
                            ? rawPalette.base.greys[300]
                            : rawPalette.base.brandEmerald[500]
                    }
                />
            }
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setDrawerOpen(true)}
        >
            {buttonText}
        </Button>
    );

    return (
        <>
            {buttonDisabled ? (
                <Tooltip
                    title={t(
                        "AiDescription_SuggestDescription_DisabledTooltip"
                    )}
                >
                    {button}
                </Tooltip>
            ) : (
                button
            )}
            <Drawer
                open={drawerOpen}
                onOpenChange={handleDrawerOpenChange}
                onOpenAutoFocus={(e) => {
                    e.preventDefault();
                    focusInput();
                }}
                variant="ai"
                preventIndirectClose
                title={t("AiDescription_DrawerTitle")}
                infoTooltipText={t("AiDescription_InfoTooltip")}
                closeAriaLabel={t("AiDescription_Cancel")}
                betaLabel={t("AiDescription_Beta")}
            >
                <div className={styles.drawerContainer}>
                    <div
                        ref={responseContainerRef}
                        className={styles.responseContainer}
                    >
                        {conversations.length === 0 ? (
                            <span className={styles.greyText}>
                                <Trans i18nKey={"AiDescription_AIHelpText"} />
                            </span>
                        ) : null}
                        {showPreview ? (
                            <div className={styles.previewContainer}>
                                <OverflowText
                                    collapsed={previewCollapsed}
                                    onToggle={() =>
                                        setPreviewCollapsed(!previewCollapsed)
                                    }
                                    heightPx={PREVIEW_MAX_COLLAPSED_HEIGHT}
                                    fadeColor={rawPalette.base.brandBlue[50]}
                                    expandLabel={t("ToggleExpand_Expand")}
                                    collapseLabel={t("ToggleExpand_Collapse")}
                                >
                                    <div className={styles.previewContent}>
                                        {renderMarkdown(previewText)}
                                    </div>
                                </OverflowText>
                            </div>
                        ) : null}
                        <AnimatePresence mode="wait">
                            {conversations.length === 0 ? (
                                <motion.div
                                    key="quick-prompts"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{
                                        duration: rawTransition.time / 1000,
                                    }}
                                    className={styles.content}
                                >
                                    <AIQuickPrompts
                                        title={t("AiDescription_QuickPrompts")}
                                        prompts={quickPrompts}
                                    />
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="prompt-response"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{
                                        duration: rawTransition.time / 1000,
                                    }}
                                >
                                    <AIPromptResponse
                                        {...getResponseLabels(t)}
                                        userPromptsAndResponses={conversations}
                                        onRevertClick={handleRevert}
                                        onRetryClick={handleRetry}
                                        onInsertClick={handleInsert}
                                        insertLabel={t("AiDescription_Insert")}
                                        onStateChanged={scrollToBottom}
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                <div className={styles.bottomContainer}>
                    <AIPromptInput
                        ref={inputRef}
                        disabled={isPending}
                        loading={isPending}
                        placeholder={t("AiDescription_PromptPlaceholder")}
                        onSubmit={submitPrompt}
                    />
                </div>
            </Drawer>
        </>
    );
}

function withResult(
    conversations: AIUserPromptAndResponse[],
    id: string,
    result: AIResult
): AIUserPromptAndResponse[] {
    return conversations.map(
        (c): AIUserPromptAndResponse =>
            c.id === id ? { id: c.id, userPrompt: c.userPrompt, result } : c
    );
}

function withError(
    conversations: AIUserPromptAndResponse[],
    id: string
): AIUserPromptAndResponse[] {
    return conversations.map(
        (c): AIUserPromptAndResponse =>
            c.id === id
                ? { id: c.id, userPrompt: c.userPrompt, error: true }
                : c
    );
}

function withLoading(
    conversations: AIUserPromptAndResponse[],
    id: string
): AIUserPromptAndResponse[] {
    return conversations.map(
        (c): AIUserPromptAndResponse =>
            c.id === id ? { id: c.id, userPrompt: c.userPrompt } : c
    );
}

function toChatHistory(
    conversations: AIUserPromptAndResponse[]
): PromptResponse[] {
    return conversations.flatMap((c) =>
        c.result ? [{ prompt: c.userPrompt, response: c.result }] : []
    );
}

function toSelectionPayload(
    selection: MarkdownSelectionRange | undefined
): DescriptionSelection | null {
    if (!selection) {
        return null;
    }
    return { start: selection.start, end: selection.end };
}

const getEditingPrompts = (t: TFunction) => [
    { label: t("AiDescription_QuickPrompt_Rewrite"), icon: <EditOutlined /> },
    {
        label: t("AiDescription_QuickPrompt_ShortenThis"),
        icon: <ScissorOutlined />,
    },
    {
        label: t("AiDescription_QuickPrompt_MakeItAList"),
        icon: <UnorderedListOutlined />,
    },
    { label: t("AiDescription_QuickPrompt_Summarize"), icon: <ReadOutlined /> },
];

const getSuggestPrompts = (t: TFunction) => [
    {
        label: t("AiDescription_QuickPrompt_MakeADraftSuggestion"),
        icon: <FileTextOutlined />,
    },
    {
        label: t("AiDescription_QuickPrompt_SuggestBullets"),
        icon: <UnorderedListOutlined />,
    },
];

const getResponseLabels = (t: TFunction) => ({
    loadingLabel: t("AiDescription_Thinking"),
    copiedTooltipLabel: t("AiDescription_Copied"),
    copyTooltipLabel: t("AiDescription_Copy"),
    revertTooltipLabel: t("AiDescription_Revert"),
    emptyLabel: t("AiDescription_Empty"),
    errorLabel: t("AiDescription_Error"),
    retryLabel: t("AiDescription_Retry"),
});

const getButtonText = (
    t: TFunction,
    description: string,
    selectedText?: string
) => {
    if (selectedText) {
        return t("AiDescription_EditSelectedText");
    }
    if (description.length > 0) {
        return t("AiDescription_ImproveDescription");
    }
    return t("AiDescription_SuggestDescription");
};
